import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { trpc } from "../../../lib/trpc";
import {
  Badge,
  Button,
  Card,
  CardTitle,
  DeletingOverlay,
  DropdownMenu,
  Icon,
  IconButton,
  MenuItem,
  Money,
  Row,
  useToast,
} from "../../ui";
import { DeleteInvoiceDialog } from "../dialogs/DeleteInvoiceDialog";
import { UploadInvoiceDialog } from "../dialogs/UploadInvoiceDialog";
import { ViewInvoiceExpensesDialog } from "../dialogs/ViewInvoiceExpensesDialog";
import type { Expense, Member, Settlement } from "../types";

interface InvoicesSectionProps {
  settlementId: string;
  invoices: Settlement["invoices"];
  expenses: Expense[];
  members: Member[];
  categories: Array<{
    id: string;
    name: string;
    icon: string;
    color: string;
    defaultShared: boolean;
  }>;
  currency: string;
  isLocked: boolean;
}

export function InvoicesSection({
  settlementId,
  invoices,
  expenses,
  members,
  categories,
  currency,
  isLocked,
}: InvoicesSectionProps) {
  const utils = trpc.useUtils();
  const { show: showToast } = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState<Settlement["invoices"][number] | null>(null);
  const [deleting, setDeleting] = useState<Settlement["invoices"][number] | null>(null);
  const [pendingDeletions, setPendingDeletions] = useState<Set<string>>(() => new Set());

  const removeInvoice = trpc.settlement.removeInvoice.useMutation({
    onMutate: ({ invoiceId }) => {
      setPendingDeletions((prev) => {
        const next = new Set(prev);
        next.add(invoiceId);
        return next;
      });
    },
    onSuccess: async (_, { invoiceId }) => {
      await utils.settlement.get.invalidate({ settlementId });
      setPendingDeletions((prev) => {
        if (!prev.has(invoiceId)) return prev;
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    },
    onError: (err, { invoiceId }) => {
      setPendingDeletions((prev) => {
        if (!prev.has(invoiceId)) return prev;
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
      showToast({
        tone: "danger",
        title: t.settlement.invoices.deleteFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
  });

  const confirmDelete = (invoice: Settlement["invoices"][number]) => {
    removeInvoice.mutate({ settlementId, invoiceId: invoice.id });
    setDeleting(null);
  };

  const expensesByInvoice = useMemo(() => {
    const map = new Map<string, Expense[]>();
    for (const e of expenses) {
      if (!e.invoiceId) continue;
      const list = map.get(e.invoiceId) ?? [];
      list.push(e);
      map.set(e.invoiceId, list);
    }
    return map;
  }, [expenses]);

  return (
    <Card padding="md">
      <Row justify="between" className="mb-3">
        <CardTitle className="flex items-center gap-2">
          <Icon name="file-text" size="sm" className="text-ink-500" />
          {t.settlement.invoices.heading}
          <Badge tone="neutral">{invoices.length}</Badge>
        </CardTitle>
        {!isLocked && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft="upload"
            onClick={() => setUploadOpen(true)}
          >
            {t.settlement.invoices.upload}
          </Button>
        )}
      </Row>

      {invoices.length === 0 ? (
        <p className="text-ink-500 text-sm">{t.settlement.invoices.empty}</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {invoices.map((inv) => {
            const linkedCount = expensesByInvoice.get(inv.id)?.length ?? 0;
            const isPending = pendingDeletions.has(inv.id);
            return (
              <Card key={inv.id} padding="md" className="relative overflow-hidden">
                {isPending && <DeletingOverlay label={t.settlement.invoices.deletingOverlay} />}
                <div className="flex flex-col gap-2">
                  {/* Topp: ikon, filnavn (truncate), actions-meny */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 dark:bg-primary/20">
                      <Icon name="file-text" size="sm" className="text-ink-500" />
                    </div>
                    <div
                      className="min-w-0 flex-1"
                      title={t.settlement.invoices.filenameTooltip(inv.filename)}
                    >
                      <span className="block truncate font-medium text-ink-900 text-sm">
                        {inv.filename}
                      </span>
                      <span className="mt-0.5 block text-ink-500 text-xs tabular-nums">
                        {t.settlement.invoices.transactionCount(inv.transactionCount)} ·{" "}
                        <Money amount={inv.totalAmount} currency={inv.currency} />
                      </span>
                    </div>
                    <div className="-mt-1 -mr-1 shrink-0">
                      <DropdownMenu
                        trigger={<IconButton icon="more-vertical" label={t.common.more} />}
                      >
                        <MenuItem icon="eye" onClick={() => setViewing(inv)}>
                          {t.settlement.invoices.rowActions.viewExpenses}
                        </MenuItem>
                        {!isLocked && (
                          <MenuItem icon="trash" destructive onClick={() => setDeleting(inv)}>
                            {t.settlement.invoices.rowActions.delete}
                          </MenuItem>
                        )}
                      </DropdownMenu>
                    </div>
                  </div>
                  {/* Status: badge i egen rad — konkurrerer ikke med filnavnet */}
                  {linkedCount > 0 && (
                    <div className="flex">
                      <Badge tone="success" iconLeft="check-circle">
                        {t.settlement.invoices.addedBadge}
                      </Badge>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <UploadInvoiceDialog
        settlementId={settlementId}
        members={members}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      <ViewInvoiceExpensesDialog
        invoice={viewing}
        expenses={viewing ? (expensesByInvoice.get(viewing.id) ?? []) : []}
        members={members}
        categories={categories}
        currency={currency}
        onClose={() => setViewing(null)}
      />

      <DeleteInvoiceDialog
        invoice={deleting}
        expenseCount={deleting ? (expensesByInvoice.get(deleting.id)?.length ?? 0) : 0}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </Card>
  );
}
